import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import type { MatterMember } from '@/types/base';

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

export type FirmSortOrder = 'asc' | 'desc';

export type FirmMatterSortField = 'name' | 'region' | 'documents_count' | 'messages_count';

export interface FirmMatterItem {
  matter_id: number;
  matter_guid: string;
  matter_name: string;
  matter_no: string | null;
  case_reference: string | null;
  region: string | null;
  forum: string | null;
  is_active: boolean;
  documents_count: number;
  messages_count: number;
  users_count: number;
  members: MatterMember[];
}

export interface FirmMattersResponse {
  matters: FirmMatterItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export const FIRM_MATTERS_PAGE_SIZE = 50;

export const useFirmMatters = (options: {
  q?: string;
  memberGuids?: string[];
  sortField?: FirmMatterSortField;
  sortOrder?: FirmSortOrder;
  page?: number;
  pageSize?: number;
}) => {
  const q = options.q?.trim() ?? '';
  const memberGuids = options.memberGuids ?? [];
  const sortField = options.sortField ?? 'documents_count';
  const sortOrder = options.sortOrder ?? 'desc';
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? FIRM_MATTERS_PAGE_SIZE;

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  for (const guid of memberGuids) {
    params.append('member_guid', guid);
  }
  params.set('sort_field', sortField);
  params.set('sort_order', sortOrder);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  const key = `/firm/matters?${params.toString()}`;
  const { data, isLoading, error, mutate } = useSWR<FirmMattersResponse>(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export type FirmSyncSortField = 'provider' | 'owner' | 'last_synced';

export interface FirmSyncItem {
  id: number;
  matter_guid: string;
  matter_name: string;
  matter_no: string | null;
  case_reference: string | null;
  provider_type: string;
  provider_label: string;
  source_label: string | null;
  destination_label: string | null;
  is_active: boolean;
  status: string;
  created_by_user_name: string | null;
  created_by_user_email: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export interface FirmSyncsResponse {
  syncs: FirmSyncItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export const FIRM_SYNCS_PAGE_SIZE = 50;

/** Fixed provider options for the Syncs tab filter (not derived from the current page). */
export const FIRM_SYNC_PROVIDER_OPTIONS = ['iManage', 'OneDrive', 'SharePoint', 'Outlook'] as const;

/** Fixed status options for the Syncs tab filter (not derived from the current page). */
export const FIRM_SYNC_STATUS_OPTIONS = [
  'Active',
  'Inactive',
  'Sync Blocked - OneDrive Reconnection Required',
  'Sync Blocked - SharePoint Reconnection Required',
  'OneDrive folder no longer exists',
  'SharePoint folder no longer exists',
  'Sync Blocked - iManage Reconnection Required',
  'Sync Blocked - Invalid Source',
  'Sync Paused - Repeated Failures',
  'Sync Paused',
  'Sync Paused - Account Deactivated',
] as const;

export const useFirmSyncs = (options: {
  q?: string;
  providers?: string[];
  statuses?: string[];
  sortField?: FirmSyncSortField;
  sortOrder?: FirmSortOrder;
  page?: number;
  pageSize?: number;
}) => {
  const q = options.q?.trim() ?? '';
  const providers = options.providers ?? [];
  const statuses = options.statuses ?? [];
  const sortField = options.sortField ?? 'last_synced';
  const sortOrder = options.sortOrder ?? 'desc';
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? FIRM_SYNCS_PAGE_SIZE;

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  for (const provider of providers) {
    params.append('provider', provider);
  }
  for (const status of statuses) {
    params.append('status', status);
  }
  params.set('sort_field', sortField);
  params.set('sort_order', sortOrder);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  const key = `/firm/syncs?${params.toString()}`;
  const { data, isLoading, error, mutate } = useSWR<FirmSyncsResponse>(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};
